import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ChildTypes, Dependent, DependentTypes } from '../../dependents/dependents-model';
import { AccountInfoRoutes } from '../../../constants/routes';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { AnnualEnrollmentContext, AnnualEnrollmentKey, AnnualEnrollmentState } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { firstDayOfNextCalendarYear, getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { isDependentAgedOut, isDependentAgingOut, getChildType } from '../../../../annual-enrollment/helpers/annual-enrollment-helpers';

interface ItemProps {
	dependent: Dependent;
	dependentType: DependentTypes;
	basePath: string;
	isReadonly?: boolean;
}
export function DependentDisplay(props: ItemProps) {
	const { dependent, dependentType, basePath, isReadonly } = props;

	const app = useContext(DemographicsContext);
	const isAnnualEnrollment = useContext(AnnualEnrollmentContext) === AnnualEnrollmentKey.annualEnrollment;
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const aeState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state?.annualEnrollment[app]);
	const ineligibleDependent = aeState?.AEInfo?.eligible_dependents?.find(d => d?.person?._guid === dependent?.person?.person_guid);

	const getLabelByDependentType = (type: DependentTypes) => {
		switch (type) {
			case DependentTypes.child:
				return epiProps.relationshipsSettingState?.epiContent?.childLabel || DependentTypes.child;
			case DependentTypes.domesticPartner:
				return epiProps.relationshipsSettingState?.epiContent?.domesticPartnerLabel || DependentTypes.domesticPartner;
			default:
				return 'Dependent';
		}
	};

	const getStartDateLabel = () => {
		return getChildType(dependent) === ChildTypes.adopted ? epiProps.relationshipsSettingState?.epiContent?.adoptionStartDateLabel : epiProps.relationshipsSettingState?.epiContent?.relationshipStartDateLabel;
	};

	const View = () => {
		if (!dependent) return null;

		if (dependentType === DependentTypes.domesticPartner) {
			return (
				<>
					<p>{`${dependent.person?.first_name} ${dependent.person?.last_name}`}<br />
						<span className='grey-caption'>{`${epiProps.relationshipsSettingState?.epiContent?.relationshipStartDateLabel || 'Relationship Start Date'}: ${getFormattedDate(dependent?.start_date)}`}</span><br />
						{(dependent?.end_date && !dependent?.date_of_death) && <span className='grey-caption'>{`${epiProps.relationshipsSettingState?.epiContent?.relationshipEndDateLabel || 'Relationship End Date'}: ${getFormattedDate(dependent?.end_date)}`}</span>}
						{dependent?.date_of_death && <span className='grey-caption'>{`${epiProps.relationshipsSettingState?.epiContent?.deathDateLabel || 'Date of Death'}: ${getFormattedDate(dependent?.date_of_death)}`}</span>}

					</p>
				</>
			);
		}
		return (
			<>
				<p>{`${dependent.person?.first_name} ${dependent.person?.last_name}`}<br />
					<span className='grey-caption'>{getLabelByDependentType(dependentType)}</span><br />
					<span className='grey-caption'>{`${epiProps.labelSettingState?.epiContent?.dobLabel || 'Date of Birth'}: ${dependent.person?.masked_date_of_birth}`}</span><br />
					{(dependent.relationship_type === ChildTypes.step || getChildType(dependent) === ChildTypes.adopted) && <span className='grey-caption'>{`${getStartDateLabel()}: ${getFormattedDate(dependent?.start_date)}`}</span>}
				</p>
			</>
		);
	};

	const Edit = () => {
		if (isReadonly) return null;

		if (dependentType === DependentTypes.domesticPartner) {
			return null;
		}

		return (
			<p><Link to={{
				pathname: `${basePath}${AccountInfoRoutes.DependentSegment}`,
				state: {
					dependentType,
					dependentNumber: dependent.person_guid,
				},
			}}>{epiProps.labelSettingState?.epiContent?.editLabel || 'Edit'}</Link></p>
		);
	};

	return (
		<div className='rich-text'>
			<View />
			<Edit />
			{
				(isDependentAgingOut(ineligibleDependent) && isAnnualEnrollment && !isReadonly) &&

				<p className='red-text'><strong>{epiProps?.annualEnrollmentSettingsState?.epiContent?.ineligibleDependentsWarning?.replace('{date}', getFormattedDate(firstDayOfNextCalendarYear()?.toString()))}</strong></p>
			}
			{
				(isDependentAgedOut(ineligibleDependent) && isAnnualEnrollment) &&
				<p className='red-text'><strong>{epiProps?.annualEnrollmentSettingsState?.epiContent?.dependentAgedOutWarningMessage}</strong></p>
			}
		</div>

	);
}

DependentDisplay.defaultProps = {
	isReadonly: false,
};